import type { Block } from "../../../shared/schema";
import { getLayoutClasses } from "../../../shared/schema";
import { useBlockVisibility } from "../../hooks/useBlockVisibility";
import { useTemplateContext } from "../../hooks/useTemplateContext";
import { interpolateBlockProps } from "./interpolateBlockProps";
import { TextBlock } from "./TextBlock";
import { HeadingBlock } from "./HeadingBlock";
import { ButtonBlock } from "./ButtonBlock";
import { CardBlock } from "./CardBlock";
import { SpacerBlock } from "./SpacerBlock";
import { DividerBlock } from "./DividerBlock";
import { BannerBlock } from "./BannerBlock";
import { HtmlBlock } from "./HtmlBlock";
import { RichTextBlock } from "./RichTextBlock";
import { PopupBlock } from "./PopupBlock";
import { AppointmentListBlock } from "./AppointmentListBlock";
import { OrderListBlock } from "./OrderListBlock";
import { ChatRoomListBlock } from "./ChatRoomListBlock";
import { FileListBlock } from "./FileListBlock";
import { FormResponseListBlock } from "./FormResponseListBlock";
import { PinnedFormsListBlock } from "./PinnedFormsListBlock";
import { ProfileEditBlock } from "./ProfileEditBlock";
import { ContentListBlock } from "./ContentListBlock";
import { PurchaseListBlock } from "./PurchaseListBlock";
import { FileUploadBlock } from "./FileUploadBlock";
import { StripeSubscriptionsBlock } from "./StripeSubscriptionsBlock";
import { StripeBillingPortalButtonBlock } from "./StripeBillingPortalButtonBlock/StripeBillingPortalButtonBlock";
import { AppointmentBookingBlock } from "./AppointmentBookingBlock";
import { VitalDataBlock } from "./VitalDataBlock/VitalDataBlock";
import { SectionBlock } from "./SectionBlock";
import { IframeBlock } from "./IframeBlock";

interface BlockRendererProps {
  block: Block;
}

/**
 * Renders the content of a block based on its type.
 * This is separated from the layout wrapper for clarity.
 */
const BlockContent: React.FC<{ block: Block }> = ({ block }) => {
  switch (block.type) {
    case "text":
      return <TextBlock content={block.props.content} variant={block.props.variant} />;

    case "heading":
      return <HeadingBlock text={block.props.text} level={block.props.level} />;

    case "button":
      return <ButtonBlock text={block.props.text} variant={block.props.variant} href={block.props.href} />;

    case "card":
      return <CardBlock title={block.props.title} description={block.props.description} />;

    case "spacer":
      return <SpacerBlock size={block.props.size} />;

    case "divider":
      return <DividerBlock />;

    case "banner":
      return (
        <BannerBlock
          content={block.props.content}
          variant={block.props.variant}
          dismissible={block.props.dismissible}
        />
      );

    case "html":
      return <HtmlBlock content={block.props.content} />;

    case "rich-text":
      return <RichTextBlock content={block.props.content} />;

    case "popup":
      return <PopupBlock block={block} />;

    case "appointment-list":
      return <AppointmentListBlock block={block} />;

    case "order-list":
      return <OrderListBlock block={block} />;

    case "chatroom-list":
      return <ChatRoomListBlock block={block} />;

    case "file-list":
      return <FileListBlock block={block} />;

    case "form-response-list":
      return <FormResponseListBlock block={block} />;

    case "pinned-forms-list":
      return <PinnedFormsListBlock block={block} />;

    case "profile-edit":
      return <ProfileEditBlock block={block} />;

    case "content-list":
      return <ContentListBlock block={block} />;

    case "purchase-list":
      return <PurchaseListBlock block={block} />;

    case "file-upload":
      return <FileUploadBlock block={block} />;

    case "stripe-subscriptions":
      return <StripeSubscriptionsBlock block={block} />;

    case "stripe-billing-portal-button":
      return <StripeBillingPortalButtonBlock block={block} />;

    case "appointment-booking":
      return <AppointmentBookingBlock block={block} />;

    case "vital-data":
      return <VitalDataBlock block={block} />;

    case "section":
      return <SectionBlock block={block} />;

    case "iframe":
      return <IframeBlock block={block} />;

    default: {
      // Exhaustive check - TypeScript will error if we miss a case
      const _exhaustive: never = block;
      return null;
    }
  }
};

/**
 * Renders a block with its responsive layout wrapper.
 * The block is wrapped in a div that applies the appropriate grid column spans
 * based on the block's layout configuration.
 *
 * Blocks with visibility conditions are only rendered if the condition evaluates to true.
 * While visibility data is loading, blocks are shown to prevent layout shift.
 */
export const BlockRenderer: React.FC<BlockRendererProps> = ({ block }) => {
  const { isVisible } = useBlockVisibility(block);
  const templateContext = useTemplateContext();

  // Hidden blocks render nothing
  if (!isVisible) {
    return null;
  }

  // Interpolate template variables in block props (e.g., {{enduser.fname}})
  const interpolatedBlock = interpolateBlockProps(block, templateContext);

  const layoutClasses = getLayoutClasses(block.layout);

  return (
    <div className={layoutClasses}>
      <BlockContent block={interpolatedBlock} />
    </div>
  );
};
